import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShoppingCart, FileText, Pencil } from "lucide-react";
import CaseFormDialog from "../../components/reseller/CaseFormDialog";
import { useModules } from "../../context/ModuleContext";
import ModuleReadOnlyNotice from "../../components/reseller/ModuleReadOnlyNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const PRINT_LABEL = {
  fingerprint: "Fingerprint", handfoot: "Hand & Foot", nose: "Nose",
  paw: "Paw", picture: "Photo", signature: "Signature",
};
const FINGER_LABEL = {
  "01": "Left Baby", "02": "Left Ring", "03": "Left Middle", "04": "Left Index", "05": "Left Thumb",
  "06": "Right Thumb", "07": "Right Index", "08": "Right Middle", "09": "Right Ring", "10": "Right Baby",
};
const TARGET_LABEL = {
  left_hand: "Left Hand", right_hand: "Right Hand", left_foot: "Left Foot", right_foot: "Right Foot",
  front_left: "Front Left Paw", front_right: "Front Right Paw", back_left: "Back Left Paw", back_right: "Back Right Paw",
};
const describeTarget = (t) => {
  if (!t) return "Print";
  if (t.finger) return `Finger · ${FINGER_LABEL[t.finger] || t.finger}`;
  if (t.target) return TARGET_LABEL[t.target] || t.target;
  return PRINT_LABEL[t.kind] || t.kind || "Print";
};

export default function PrintRecordDetail() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  // Infinity Touch off: the case stays viewable, editing and ordering do not.
  const { canWrite } = useModules();
  const readOnly = !canWrite("infinity_touch");
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/memorial-records/${recordId}`);
        setRecord(res.data);
      } catch (e) {
        toast.error("Failed to load case details");
      } finally {
        setLoading(false);
      }
    })();
  }, [recordId]);

  if (loading) return <div className="py-16 text-center text-slate-400">Loading…</div>;
  if (!record) return <div className="py-16 text-center text-slate-400">Case not found.</div>;

  const s = record.subject || {};
  const name = [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="print-record-detail">
      <ModuleReadOnlyNotice module="infinity_touch" />

      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/reseller/print-records")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6" /> Case {record.case_number}
          </h2>
          <p className="text-slate-500">{PRINT_LABEL[record.print_type] || record.print_type} ·{" "}
            <Badge variant="outline" className={record.status === "ordered" ? "border-green-200 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-600"}>
              {record.status === "ordered" ? "Ordered" : "Saved"}
            </Badge>
          </p>
        </div>
        {!readOnly && (<>
        <Button variant="outline" className="rounded-xl" onClick={() => setEditOpen(true)}>
          <Pencil className="w-4 h-4 mr-1" /> Edit details
        </Button>
        <Button className="rounded-xl" onClick={() => navigate(`/reseller/print-records/${recordId}/order`)}>
          <ShoppingCart className="w-4 h-4 mr-1" /> Place Order
        </Button>
        </>)}
      </div>

      <CaseFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        record={record}
        onSaved={setRecord}
      />

      {/* Subject details */}
      <div className="card-elevated p-5">
        <h3 className="font-semibold text-slate-800 mb-3">{s.subject_type === "pet" ? "Pet" : "Deceased"} Details</h3>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Field label="Name" value={name || "—"} />
          <Field label="Subject Type" value={s.subject_type === "pet" ? "Pet" : "Human"} />
          <Field label="Gender" value={s.gender || "—"} />
          <Field label="Date of Birth" value={fmtDate(s.date_of_birth)} />
          <Field label="Date of Death" value={fmtDate(s.date_of_death)} />
          <Field label="Time of Death" value={s.time_of_death || "—"} />
          <Field label="Place of Death" value={s.place_of_death || "—"} />
          <Field label="Created" value={fmtDate(record.created_at)} />
        </div>
        {record.notes && <p className="text-sm text-slate-500 mt-3"><span className="font-medium">Notes:</span> {record.notes}</p>}
      </div>

      {/* Captures */}
      <div className="card-elevated p-5">
        <h3 className="font-semibold text-slate-800 mb-3">Captured Prints ({(record.captures || []).length})</h3>
        {(record.captures || []).length === 0 ? (
          <p className="text-sm text-slate-400">No prints captured yet.</p>
        ) : (
          <div className="flex flex-wrap gap-4">
            {record.captures.map((cap, i) => {
              const url = cap.image_url ? (cap.image_url.startsWith("http") ? cap.image_url : `${process.env.REACT_APP_BACKEND_URL}${cap.image_url}`) : null;
              return (
                <div key={cap.capture_id || i} className="text-center">
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img src={getImageUrl(cap.image_url)} alt={describeTarget(cap.target)} className="w-24 h-24 object-cover rounded-lg border" />
                    </a>
                  ) : (
                    <div className="w-24 h-24 rounded-lg border bg-slate-50 flex items-center justify-center text-slate-300 text-xs">No image</div>
                  )}
                  <p className="text-[11px] text-slate-600 mt-1 max-w-[96px]">{describeTarget(cap.target)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-50 py-1">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 text-right">{value}</span>
    </div>
  );
}
